<template>
    <detail-header
        pretitle="Entitlements"
        :title="snapshot ? snapshot.name : '&nbsp;'"
        :editable="true"
        :editMode="editMode"
        :editRoles="editRoles"
        :fields="fields"
        v-model="entitlement"
        @editClick="onEditClick"
        @deleteClick="onDeleteClick"
        @saveClick="onSaveClick"
        @cancelClick="onCancelClick"
        @closeClick="$router.push('/admin/entitlements')"
    >
    </detail-header>
    <div class="container-fluid">
        <div v-if="error" class="alert alert-danger" role="alert">
            {{ error }}
        </div>
        <div v-if="entitlement && snapshot">
            <div class="card catalog-reveal">
                <div class="card-header">
                    <h4 class="card-header-title">Entitlement details</h4>
                    <span v-if="editMode" class="catalog-editing">Editing</span>
                    <span v-else-if="auditItems.length" class="catalog-audit" :title="auditItems.join('\n')">{{ auditItems[auditItems.length - 1] }}</span>
                </div>
                <property-grid v-if="!editMode" :groups="viewGroups" :record="entitlement" :links="viewLinks" :monoFields="['metrics_key']" />
                <div v-else class="catalog-edit">
                    <section v-for="section in fieldSections" :key="section.key" class="catalog-edit-group">
                        <h6 class="catalog-edit-title">{{ section.title }}</h6>
                        <div class="row">
                            <form-fields :fields="section.compact" v-model="entitlement" :editMode="editMode"
                                colField="col-12 col-md-6 col-xl-4" />
                            <form-fields v-if="section.wide.length" :fields="section.wide" v-model="entitlement" :editMode="editMode"
                                colField="col-12" />
                        </div>
                    </section>
                </div>
            </div>
            <div class="card catalog-reveal catalog-reveal-2">
                <div class="card-header">
                    <h4 class="card-header-title">
                        Entitlements mapped to {{ snapshot.name }}<span class="catalog-count">{{ subEntitlements.length }}</span>
                    </h4>
                </div>
                <div v-if="subEntitlements.length" class="card-body">
                    <Datatable
                        :tableId="`${entitlement.name}Table`"
                        :columns="columns"
                        uniqueField ="entitlement_id"
                        :data="subEntitlements"
                        :showLoading="false"
                        :deletable="$store.getters.userRoles.isAqferAdmin"
                        :compact="true"
                        :selectCheckboxes="$store.getters.userRoles.isAqferAdmin"
                        :detailColumn="{renderBaseUrl: (data) => `/admin/entitlements/${encodeURIComponent(data.name)}/versions/${data.version}`, renderParams: (data) => ({name: data.name, version: data.version}), name: 'Entitlement'}"
                    />
                </div>
                <div v-else class="catalog-empty">
                    <span class="catalog-empty-icon"><i class="fa fa-diagram-project"></i></span>
                    <p>No entitlements use {{ snapshot.name }} as their base entitlement.</p>
                </div>
            </div>
        </div>
        <div v-if="loading || (!entitlement && !error)" class="text-center">
            <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>
    </div>
</template>
<script>
import DetailHeader from '../../components/DetailHeader.vue';
import Datatable from '../../components/Datatable.vue';
import Fields from "../../utils/field-constants/entitlementFields";
import { mapActions } from 'vuex';
import FormFields from '../../components/FormFields.vue';
import PropertyGrid from '../../components/admin/PropertyGrid.vue';
import { nameCell, versionCell, softCell, auditSummary } from '../../utils/catalogCells';
const AUDIT_FIELDS = ["created_by", "created_at", "updated_by", "updated_at"];
const FIELD_SECTIONS = [
    { key: "definition", title: "Definition", names: ["name", "version", "metrics_key", "unit", "description"] },
    { key: "metering", title: "Metering", names: ["quantity", "aggregation", "multiple_allowed"] },
    { key: "base", title: "Base entitlement", names: ["base_entitlement_id", "base_qty_consumed", "conversion_floor", "qty_included_w_base"] }
];
export default{
    name: "Entitlement",
    components: { DetailHeader, FormFields, Datatable, PropertyGrid },
    data(){
        return{
            editMode: false,
            editRoles: [1],
            entitlement: null,
            snapshot: null,
            error: null,
            fields: Fields,
            columns:[
                { data: "name", render: nameCell },
                { data: "version", render: versionCell },
                { data: "unit", render: softCell },
                { data: "quantity"}
            ],
            subEntitlementList:[],
            loading:false
        }
    },
    computed:{
        entitlementList(){
            return this.$store.state.admin.entitlements
        },
        subEntitlements() {
            return this.subEntitlementList || [];
        },
        fieldSections() {
            const grouped = FIELD_SECTIONS.flatMap((section) => section.names);
            return FIELD_SECTIONS.map((section) => {
                const fields = this.fields.filter((field) => section.names.includes(field.name) ||
                    (section.key === "definition" && !grouped.includes(field.name) && !AUDIT_FIELDS.includes(field.name)));
                return {
                    ...section,
                    compact: fields.filter((field) => field.type !== "textarea"),
                    wide: fields.filter((field) => field.type === "textarea")
                };
            });
        },
        baseEntitlement() {
            const baseId = this.snapshot && this.snapshot.base_entitlement_id;
            return baseId ? this.entitlementList.find((item) => item.entitlement_id === baseId) : null;
        },
        auditItems() {
            return auditSummary(this.snapshot);
        },
        viewGroups() {
            return this.fieldSections.map((section) => ({ title: section.title, fields: [...section.compact, ...section.wide] }));
        },
        viewLinks() {
            return this.baseEntitlement ? { base_entitlement_id: this.entitlementRoute(this.baseEntitlement) } : {};
        }
    },
    watch:{
        "entitlement.entitlement_id":{
            handler: function(newVal){
                if(newVal){
                    this.fields.forEach((field)=>{
                        if(field.name=="base_entitlement_id"){
                            let options=[{label: "None", value: null}] 
                            this.entitlementList.forEach((item)=>{
                                if(item.entitlement_id !== newVal)
                                    options.push({label: item.name, value: item.entitlement_id})
                            })
                            field.options=options 
                        }
                    })
                }
            }
        }
    },
    beforeMount(){
        this.initialize();
    },
    methods:{
        ...mapActions({
            readEntitlement:"admin/readEntitlement",
            updateEntitlement:"admin/updateEntitlement",
            deleteEntitlement:"admin/deleteEntitlement",
            showModal: "showModal",
            getSubEntitlementList: "admin/getSubEntitlementList"
        }),
        entitlementRoute(item) {
            return `/admin/entitlements/${encodeURIComponent(item.name)}/versions/${item.version}`;
        },
        setEditMode(mode){
            this.editMode =mode;
            this.error= null;
        },
        async fetchEntitlement(reload=false){
            let response={};
            //force reload when to view base entitlement in detail page else load from cache if available
            const cached = this.$route.params.data ? JSON.parse(this.$route.params.data) : null;
            if(!cached || cached.version != this.$route.params.version || reload){
                try{
                    response = await this.readEntitlement({ name: this.$route.params.name, version: this.$route.params.version });
                } catch (error){
                    if(error.response.data.error)
                        this.error = error.response.data.error;
                    else this.error = error.message;
                }
            }else{
                response["data"] = cached
            }
            this.entitlement = await response.data;
            this.snapshot = JSON.parse(JSON.stringify(this.entitlement));
        },
        async fetchSubEntitlementList(){
            let response;
            try{
                response = await this.getSubEntitlementList({ name: this.$route.params.name, version: this.$route.params.version });
            } catch (error){
                if(error.response.data.error)
                    this.error = error.response.data.error;
                else this.error = error.message;
            }
            this.subEntitlementList = await (response? response.data :[]);
        },
        onCancelClick(){
            this.setEditMode(false);
            this.entitlement = JSON.parse(JSON.stringify(this.snapshot));
            this.error =null;
        },
        onEditClick(){
            this.setEditMode(true);
        },
        onDeleteClick(){
            const self= this;
            this.showModal({
                component: "confirm",
                data:{
                    title: "Delete Entitlement",
                    message: `Are you sure you want to delete ${this.entitlement.name}`,
                    async confirm(){
                        try{
                            const response = await self.deleteEntitlement({ name: self.snapshot.name, version: self.snapshot.version });
                            self.$router.push("/admin/entitlements");
                            return response;
                        } catch( error){
                            if (error.response.data.error)
                            self.error= error.response.data.error;
                            else self.error = error.message;
                            return;
                        }
                    }
                }
            })
        },
        async onSaveClick() {
            let self = this;
            let entitlementObj = this.entitlement;            
            try {
                const response = await this.updateEntitlement({ name: this.snapshot.name, version: this.snapshot.version, data: entitlementObj });
                if (entitlementObj.version === '' || entitlementObj.version == null)
                    entitlementObj.version = this.snapshot.version;
                this.setEditMode(false);
                this.snapshot = JSON.parse(JSON.stringify(entitlementObj));
                this.$router.replace(`/admin/entitlements/${encodeURIComponent(entitlementObj.name)}/versions/${entitlementObj.version}`);
                this.$store.dispatch('addToast', {
                    message: "Entitlement '" + entitlementObj.name + "' updated successfully.",
                    type: "success"
                });
                const refreshed = await this.readEntitlement({ name: entitlementObj.name, version: entitlementObj.version }).catch(() => null);
                if (refreshed && refreshed.data) {
                    this.entitlement = refreshed.data;
                    this.snapshot = JSON.parse(JSON.stringify(refreshed.data));
                }
            } catch (error) {
                if (error.response.data.error)
                    self.error = error.response.data.error;
                else self.error = error.message;
            }
        },
        async initialize(reload=false){
            this.fetchEntitlement(reload);
            this.fetchSubEntitlementList();
            if(!this.entitlementList.length){
                this.$store.dispatch("admin/fetchEntitlements");
            }
        },
    },
    async beforeRouteUpdate (to, from, next) {  
            if (to.params.name !== from.params.name || to.params.version !== from.params.version) {
                this.loading=true;
                this.$route.params.name=to.params.name
                this.$route.params.version=to.params.version
                this.initialize(true);
                this.loading=false;    
            }
            next();       
        },
}
</script>
<style lang="scss">
@import './catalog.scss';
</style>